// Module 08 stable public surface. Cross-feature consumers import reports only through this barrel.
import { defineAsyncComponent } from 'vue'

export { default as MatrixTable } from './components/table/MatrixTable.vue'
export { default as ReportPagination } from './components/table/ReportPagination.vue'
export { default as TrendTable } from './components/table/TrendTable.vue'
export { default as DataVisible } from './components/DataVisible.vue'
export { default as DataVisibleActions } from './components/controls/DataVisibleActions.vue'
export { default as ReportFilters } from './components/filters/ReportFilters.vue'
export { default as FilterChip } from './components/filters/FilterChip.vue'
export { default as ReportTableCustomizePanel } from './components/controls/ReportTableCustomizePanel.vue'
export { default as ReportTableModeControls } from './components/controls/ReportTableModeControls.vue'
export { default as ReportToolbarSelect } from './components/controls/ReportToolbarSelect.vue'
export { default as ReportTablePresenter } from './components/table/ReportTablePresenter.vue'
export { default as ReportTable } from './components/table/ReportTablePresenter.vue'
// The owned image-cell presenter, so a feature building its own DataTable renders report media
// (thumbnail + preview, URL resolution) through module 08 instead of re-implementing it.
export { default as MediaCell } from './components/table/cells/MediaCell.vue'
export { default as ChartRenderer } from './components/chart/ChartRenderer.vue'
export { default as ChartFieldRoles } from './components/chart/ChartFieldRoles.vue'
export { default as ChartTypePicker } from './components/chart/ChartTypePicker.vue'
export { default as ChartSettingsDrawer } from './components/chart/ChartSettingsDrawer.vue'
export { default as ViewTypeToggle } from './components/controls/ViewTypeToggle.vue'
export { default as ReportMediaItem } from './components/card/ReportMediaItem.vue'
export { default as ReportMediaGalleryCell } from './components/table/cells/MediaGalleryCell.vue'
export { default as ColumnCustomizeDrawer } from './components/controls/ColumnCustomizeDrawer.vue'
export { default as ReportChannelTabs } from './components/controls/ReportChannelTabs.vue'
export { REPORT_TOOLBAR_ICONS } from './configs/reportToolbarIcons'
export { default as ReportSectionTitle } from './components/page/ReportSectionTitle.vue'
export { default as MetricLabel } from './components/MetricLabel.vue'
export { default as Ga4Indicator } from './components/Ga4Indicator.vue'
export { toReportColumnOption, toReportColumnOptions } from './service/reportColumnOption'
// `ReportPage` is a lazy wrapper here, not a static re-export (ORP-22679, contract 05 §11):
// `app/bootstrap.ts` imports this barrel statically for its lifecycle registrants, and a plain
// `export {default} from './X.vue'` line pulls the WHOLE module — script, styles, and its own
// dependency graph — into that static chunk regardless of whether the static importer uses the
// binding (Rollup cannot prove an SFC re-export is side-effect free). `ReportPage` genuinely is
// needed, synchronously-shaped, by ~14 routed page components in other features (commerce-
// analytics, audience, marketing-insights) — so the seam stays, but the module load moves behind
// a dynamic import(), keeping ReportPage.vue out of the entry closure. Every consumer is already
// a routed (lazily-loaded) page itself, so this changes nothing about when the bytes download.
export const ReportPage = Object.assign(
  defineAsyncComponent(() => import('./components/page/ReportPage.vue')),
  { name: 'ReportPage' },
)
export { default as ReportSection } from './components/page/ReportSection.vue'
export { default as ReportTabs } from './components/page/ReportTabs.vue'
export { default as ReportDatePicker } from './components/page/ReportDatePicker.vue'
export type { ReportDatePickerHydration } from './models/datePicker.types'
export { default as SectionDropdowns } from './components/page/SectionDropdowns.vue'
export { default as CacheClearButton } from './components/controls/CacheClearButton.vue'
export { registerReportMessages } from './i18n'
export { reportRoutes } from './routes'
export { reportNavItems } from './nav'
// ── The layout registry (ledger B1 / §A11) — how 24/25/27 reach a report page without 08
// importing them. `SEED_REPORT_LAYOUTS` is deliberately NOT exported: a static map others can read
// is exactly what the inversion removes.
export {
  registerReportLayout,
  registeredReportLayoutKeys,
  reportLayout,
  resetReportLayoutRegistry,
} from './configs/layoutRegistry'
export type { ReportLayoutKey, SeedReportLayoutKey } from './configs/layouts'
// Shared request builders consumed by report-owned Demographic/TikTok layouts and module 25's
// Meta layout. Exported through the barrel so the vertical never deep-imports module 08.
export {
  META_CHANNEL_FILTER,
  TIKTOK_CHANNEL_FILTER,
  metaAudienceBuild,
  metaTargetingPerformanceBuild,
  tiktokTargetingPerformanceBuild,
} from './configs/paidSocialTargeting'
export {
  registerReportPage,
  registeredReportPages,
  reportPageByRoute,
  reportRouteForReportId,
  resetReportPageRegistry,
} from './configs/pages'
export type { ReportPageDefinition } from './configs/pages'
export { useReportLayout } from './composables/useReportLayout'
export type { UseReportLayout } from './composables/useReportLayout'
export { useSectionSelections } from './composables/useSectionSelections'
export type { UseSectionSelections } from './composables/useSectionSelections'
export { useSectionGroups } from './composables/useSectionGroups'
export type { UseSectionGroups } from './composables/useSectionGroups'
export { resolveLayout } from './service/resolveLayout'
export type { LayoutAvailabilityQuery, ResolveLayoutDeps } from './service/resolveLayout'
export { scheduleTabDetails } from './service/sectionFields'
export { SECTION_GROUP_OVERALL, SECTION_LEVELS } from './models/layoutConfig.types'
export type {
  LayoutVisibleRule,
  ReportLayoutConfig,
  ReportPageTabConfig,
  ReportSectionConfig,
  ReportSectionTabConfig,
  SectionBuild,
  SectionBuildContext,
  SectionDataVisibleConfig,
  SectionDropdown,
  SectionDropdownOption,
  SectionLevelId,
  SectionTopTab,
} from './models/layoutConfig.types'
export type {
  MatrixTableProps,
  ReportPaginationChange,
  ReportPaginationProps,
} from './components/table/types'
export { useMetricCatalog } from './composables/useMetricCatalog'
export type { UseMetricCatalog } from './composables/useMetricCatalog'
export { useLevelMetadata } from './composables/useLevelMetadata'
export type { UseLevelMetadata } from './composables/useLevelMetadata'
export { useSelectableFields } from './composables/useSelectableFields'
export type { UseSelectableFields, SelectableFieldColumns } from './composables/useSelectableFields'
export { useVisualizationTypeCatalog } from './composables/useVisualizationTypeCatalog'
export type { UseVisualizationTypeCatalog } from './composables/useVisualizationTypeCatalog'
export { useChartConfig } from './composables/useChartConfig'
export type { UseChartConfig, UseChartConfigOptions } from './composables/useChartConfig'
export { useFieldRoles } from './composables/useFieldRoles'
export type { UseFieldRoles, UseFieldRolesOptions } from './composables/useFieldRoles'
export { useReportAccess } from './composables/useReportAccess'
export type { UseReportAccess } from './composables/useReportAccess'
export { useReportData } from './composables/useReportData'
export type { UseReportData, UseReportDataOptions } from './composables/useReportData'
export { useReportPostData } from './composables/useReportPostData'
export { useReportCellFormatter } from './composables/useReportCellFormatter'
export type { UseReportCellFormatter } from './composables/useReportCellFormatter'
export { resolveRowMedia } from './service/reportMediaPresentation'
export type { ReportMediaAsset } from './service/reportMediaPresentation'
export { useReportFilters } from './composables/useReportFilters'
export type { UseReportFilters, UseReportFiltersOptions } from './composables/useReportFilters'
export { useReportFilterDropdownables } from './composables/useReportFilterDropdownables'
export type {
  LoadReportFilterDropdownValues,
  ReportFilterDropdownables,
  UseReportFilterDropdownables,
  UseReportFilterDropdownablesOptions,
} from './composables/useReportFilterDropdownables'
export { useReportSchedules } from './composables/useReportSchedules'
export type { UseReportSchedules } from './composables/useReportSchedules'
export { useDynamicDropdowns } from './composables/useDynamicDropdowns'
export type {
  DynamicDropdownAnswers,
  DynamicDropdownState,
  UseDynamicDropdowns,
} from './composables/useDynamicDropdowns'
export type {
  DynamicDropdownMap,
  DynamicDropdownQuery,
  DynamicDropdownScope,
} from './models/dynamicDropdowns.types'
export { useReportTableViewState } from './composables/useReportTableViewState'
export type {
  UseReportTableViewState,
  UseReportTableViewStateOptions,
} from './composables/useReportTableViewState'
export { useSavedReportFilters } from './composables/useSavedReportFilters'
export type { UseSavedReportFilters } from './composables/useSavedReportFilters'
export { useReportViewConfigs } from './composables/useReportViewConfigs'
export type { UseReportViewConfigs } from './composables/useReportViewConfigs'
export { useReportExport } from './composables/useReportExport'
export type { UseReportExport, UseReportExportOptions } from './composables/useReportExport'
export { useChartContractValidation } from './composables/useChartContractValidation'
export type { UseChartContractValidation } from './composables/useChartContractValidation'
export { registerReportBootstrapHydrators } from './flows/bootstrapHydrators'
export { registerReportNavResets } from './flows/navResets'
export { primeMetricCatalog, primeReportCatalogs } from './flows/primeMetricCatalog'
export {
  invalidateMetricCatalog,
  removeRuntimeMetricDefinition,
} from './flows/invalidateMetricCatalog'
export type { RuntimeMetricDefinition } from './flows/invalidateMetricCatalog'
export { registerMetricCatalogSessionReset } from './lifecycle/registerMetricCatalogSessionReset'
export { useCustomizeStore } from './stores/customizeStore'
export { preselectBudgetAllocationChannel } from './service/preselectBudgetAllocation'
// The section view-type persistence seam (contract Q4). Exported so a registrant-owned section
// (`section.component`) can persist its chart/table choice through the SAME account/workspace-scoped
// partition the config-driven `ReportSection` uses, instead of re-deriving the key in a vertical.
export { useReportViewState } from './composables/useReportViewState'
export type {
  UseReportViewState,
  UseReportViewStateOptions,
} from './composables/useReportViewState'
export type { MetricCatalogStatus } from './stores/metricCatalogStore'
export type { VisualizationTypeCatalogStatus } from './stores/visualizationTypeCatalogStore'
export { REPORT_STATUS_AVAILABLE, SUMMARY_REPORT_ID } from './models/reportAccess.types'
export type { ReportAccessItem } from './models/reportAccess.types'
export type {
  ChangeType,
  ColumnSubType,
  MetricLabelOptions,
  ReportColumn,
} from './models/column.types'
export type { ReportLevel, ReportLevelSource } from './models/level.types'
export { MATCH_MODES } from './models/filter.types'
export type {
  FilterDropdownable,
  FilterDropdownables,
  FilterDropdownableValue,
  FilterMatchMode,
  ReportFilter,
  SavedReportFilterInput,
  SavedReportFilterOption,
} from './models/filter.types'
export type {
  DataVisibleCardMediaType,
  DataVisibleCellSlotProps,
  DataVisibleComposition,
  DataVisibleControlsSlotProps,
  DataVisiblePaginationConfig,
  DataVisibleProps,
  DataVisibleTableMode,
  DataVisibleTrendConfig,
} from './models/dataVisible.types'
export type {
  ChartAvailabilityInput,
  ChartFieldCandidate,
  ChartFieldRole,
  ChartFieldRoleFields,
  ChartFieldRoleSelection,
  ChartFieldRoleSources,
  ChartFieldType,
  ChartType,
  ChartTypeMeta,
  ReportViewPreferenceV1,
  ReportViewType,
  VisualizationTypeDefinition,
} from './models/chart.types'
export { RENDERABLE_CHART_TYPES } from './models/chart.types'
export type {
  ReportTableCustomization,
  ReportTableSavedViewDraft,
  ReportTableSavedViewV1,
  ReportTableType,
} from './models/tablePresentation.types'
export type {
  BuildTrendTablePresentationInput,
  TrendTableFrequency,
  TrendTablePresentation,
  TrendTableRow,
  TrendTableRowKind,
} from './models/trendTable.types'
export type {
  ReportAsyncExportResult,
  ReportExportOutcome,
  ReportSheetExportResult,
} from './models/export.types'
export type {
  ReportTableAdditionalElementV1,
  ReportTableConfigV1,
  ReportTableDropdownV1,
  ReportTablePreferenceV1,
  ReportTableSortCycle,
  ReportTableSortDirection,
  ReportTableSortOrder,
  ReportTableSortV1,
  ResolvedReportTableConfig,
} from './models/tableConfig.types'
export type {
  ReportControllerError,
  ReportDataLoadContext,
  ReportDataLoader,
  ReportDataState,
  ReportExportFormat,
  ReportResponse,
  ReportResponsePayload,
  ReportRow,
  ReportSpec,
} from './models/report.types'
export type { ChartValidationRequest, ChartValidationResult } from './models/chartValidation.types'
export type {
  DropdownableDimension,
  ScheduleAdditionalInfo,
  ScheduleTab,
  ScheduleTabMap,
  ScheduleTabSelection,
  ScheduleTabValue,
  WorkspaceSchedule,
} from './models/schedule.types'
export { filterRows } from './service/filterRows'
export { dropdownablesFromRows } from './service/dropdownablesFromRows'
export type { DropdownablesFromRowsOptions } from './service/dropdownablesFromRows'
export {
  REPORT_FILTER_MODE_DEFINITIONS,
  getAvailableReportFilterModes,
  getDefaultReportFilterMode,
  getReportFilterModeDefinition,
} from './service/reportFilterModes'
export type {
  ReportFilterModeDefinition,
  ReportFilterModeLabelKey,
  ReportFilterModeValueShape,
} from './service/reportFilterModes'
export { calcChange } from './service/calcChange'
export { applyGa4OverrideTotal, resolveGa4OverrideFields } from './service/ga4Override'
export { mapColumns } from './service/mapColumns'
export type { MapColumnsContext } from './service/mapColumns'
export { isKeyEventChartField, isKeyEventField } from './service/columns/keyEventFields'
export { resolvePartitionColumns } from './service/columns/partitionColumns'
export { DATE_DIMENSIONS } from './service/dateDimensions'
export type {
  PartitionColumnsInput,
  PartitionColumnsResult,
  PartitionDateRange,
} from './service/columns/partitionColumns'
export { sortRows } from './service/sortRows'
export {
  applyReportTablePreference,
  findScheduleTabTableConfig,
  resolveReportTableConfig,
  resolveReportTablePreference,
} from './service/reportTableConfig'
export { buildMultiMetricChartOptions } from './service/buildMultiMetricChartOptions'
export { aggregateMultiMetricSeries } from './service/aggregateMultiMetricSeries'
export {
  multiMetricDateKey,
  multiMetricDateRange,
  multiMetricPeriodOf,
  multiMetricResponseRows,
  multiMetricValueField,
} from './service/multiMetricResponse'
export { buildCategoricalChartOptions } from './service/chart/buildCategoricalChartOptions'
export { buildChartOptions } from './service/chart/buildChartOptions'
export type { ChartOptions, ChartSpec } from './service/chart/buildChartOptions'
export type { ChartRendererProps } from './components/chart/types'
export type { ReportTableProps, TrendTableProps } from './components/table/types'
export {
  applyFieldRoleSelectionRules,
  getFieldRoleFields,
  getFieldRoleValue,
  getSavedFieldRoleFields,
  normalizeChartFieldRoles,
  toChartFieldCandidates,
} from './service/chart/fieldRoles'
export {
  buildBreakdownRows,
  buildHeatmapCells,
  buildMatrixPresentation,
  buildReportTotalRows,
  getAvailableReportTableTypes,
} from './service/tablePresentation'
export { buildTrendTablePresentation } from './service/trendTablePresentation'
export {
  chartRendererRegistry,
  fallbackChartTypeCatalog,
  getAvailableChartTypes,
  resolveChartTypeCatalog,
  isChartDataPointCountCompatible,
  normalizeVisualizationTypes,
} from './service/chart/visualizationCatalog'
export { buildAnomalyTrendOptions } from './service/buildAnomalyTrendOptions'
export type {
  AnomalyTrendChartInput,
  AnomalyTrendOptions,
  AnomalyTrendOptionsContext,
} from './service/buildAnomalyTrendOptions'
export type {
  AggregateMultiMetricSeriesInput,
  MultiMetricPeriod,
  MultiMetricSeries,
  MultiMetricSeriesMap,
} from './service/aggregateMultiMetricSeries'
export type {
  MultiMetricChartInput,
  MultiMetricChartOptions,
  MultiMetricChartSeries,
  MultiMetricFrequency,
} from './service/buildMultiMetricChartOptions'
export type { SortRowsOptions } from './service/sortRows'
export {
  BUDGET_ALLOCATION_TABS,
  SUMMARY_ACQUISITION_CHART,
  SUMMARY_ACQUISITION_TABLE_CONFIG,
  SUMMARY_CREATIVE_DIMENSIONS,
  SUMMARY_CREATIVE_METRICS,
} from './configs/summary'
